/**
 * KpiBandSection — a single 4-cell KPI row on the light page as a plain OUTLINE
 * table: one outer 2px black box (NO fill, NO shadow) split into 4 cells by
 * vertical black dividers. Each cell stacks a big `font-head` number (colorless —
 * neutral `text-foreground`) over a small `font-mono` uppercase label.
 *
 * All four figures are FIXED constants (see KPI_FIGURES below for how each was
 * derived). They count up from 0 → their value via CountUp once the band scrolls
 * into view (reduced-motion aware — they render the final value immediately when
 * reduced motion is preferred).
 *
 * Preflight is OFF, so the divider/box borders are declared `border-solid`
 * explicitly. ZERO MUI.
 */
import * as React from 'react';
import CountUp from './CountUp';
import StatCell from './shared/StatCell';

/**
 * KPI figures. Update these when the study portfolio changes.
 *
 *  PARTICIPANTS — sum of "Total Participants (N)" across the 9 studies in the
 *    StudySync study portfolio sheet (studysync_portfolio_5.xlsx, Oct 2026):
 *    800 + 50 + 300 + 20 + 30 + 50 + 80 + 120 + 5000 = 6,450.
 *
 *  SUPPORTING STUDIES — the 9 studies in that same portfolio sheet.
 *
 *  DATA POINTS — an ESTIMATE of individual time-series samples (e.g. one intraday
 *    heart-rate reading = 1 point) collected to date, ~815.4M:
 *      1. Calibration: Michael Snyder's account holds ~27M points; assuming ~3 years
 *         of near-continuous multi-device wear, that is ~24.7k points/day.
 *      2. Average participant: one wearable with partial wear time, taken as 40% of
 *         that density, ~9.9k points/day (Fitbit intraday HR alone is ~6–17k/day).
 *      3. Participant-days to date: for each portfolio study, enrollment ramps
 *         linearly over its recruitment window from its start date and each
 *         participant contributes up to their per-protocol duration. This gives
 *         ~968 enrolled and ~82.7k participant-days as of 2026-10-26. (iCARE counts
 *         only its StudySync sub-study N=30, not the 800-person parent trial.)
 *      4. 82.7k participant-days × 9.9k points/day ≈ 815.4M, shown as "815.3M+".
 *
 *  WEARABLE DATA TYPES — distinct per-source data types the backend ingests:
 *    132 Apple HealthKit (HEALTHKIT_METRIC_MAPPING, newbackend/src/routes/healthkit.js)
 *    + 35 Fitbit / Google Health (FITBIT_PROVIDER_CATALOG_ENTRIES minus the 4
 *      FITBIT_COLLECTION_EXCLUDED_DATA_TYPES, newbackend/src/services/fitbitProviderCatalog.js)
 *    + 25 Ultrahuman (primary metrics, newbackend/src/integrations/ultrahuman/catalog.js)
 *    = 192.
 */
const KPI_FIGURES = {
  participants: 6450,
  supportingStudies: 9,
  dataPointsMillions: 815.3,
  wearableDataTypes: 192,
} as const;

/** One KPI cell — every figure animates (CountUp from 0 → value). */
interface Kpi {
  label: string;
  value: number;
  /** Fixed decimal places for the animated number (default 0). */
  decimals?: number;
  /** Text after the number (e.g. "M+"). */
  suffix?: string;
  /** Tailwind text-color class for the number (neutral — colorless outline table). */
  colorClass: string;
}

const KPIS: Kpi[] = [
  { label: 'Participants', value: KPI_FIGURES.participants, colorClass: 'text-foreground' },
  { label: 'Supporting Studies', value: KPI_FIGURES.supportingStudies, colorClass: 'text-foreground' },
  {
    label: 'Data Points',
    value: KPI_FIGURES.dataPointsMillions,
    decimals: 1,
    suffix: 'M+',
    colorClass: 'text-foreground',
  },
  { label: 'Wearable Data Types', value: KPI_FIGURES.wearableDataTypes, colorClass: 'text-foreground' },
];

const KpiBandSection: React.FC = () => (
  <section className="bg-background px-4 py-16 md:px-8 md:py-20">
    <div className="mx-auto max-w-6xl">
      {/* The band as a plain OUTLINE table: one 2px black box, no fill, no
          shadow, split into 4 cells. 4 cols at md+, 2×2 on mobile. Vertical
          black dividers per-cell via left borders (suppressed at row starts). */}
      <div className="grid grid-cols-2 border-2 border-solid border-black md:grid-cols-4">
        {KPIS.map((kpi, i) => (
          <StatCell
            key={kpi.label}
            label={kpi.label}
            valueClassName={kpi.colorClass}
            size="lg"
            className={[
              'px-4 py-8 md:py-10',
              // Vertical divider on the left edge of every cell except the first
              // in each row. Mobile (2 cols): suppress on cols 0 & 2. md+ (4
              // cols): suppress only on col 0.
              i % 2 === 0 ? '' : 'border-l-2 border-solid border-black',
              i % 4 === 0 ? 'md:border-l-0' : 'md:border-l-2 md:border-solid md:border-black',
              // On mobile the bottom row needs a divider above it; add a top
              // border to the second row (indices 2,3) and remove it at md+.
              i >= 2 ? 'border-t-2 border-solid border-black md:border-t-0' : '',
            ].join(' ')}
          >
            <CountUp value={kpi.value} decimals={kpi.decimals} suffix={kpi.suffix} />
          </StatCell>
        ))}
      </div>
    </div>
  </section>
);

export default KpiBandSection;
